/**
 * Validação do campo «Endereço do menu» no console (#418).
 *
 * As regras vivem em @self-restaurant/shared (validateTenantSlug +
 * RESERVED_SLUGS + suggestSlugCandidates) — aqui só entra a camada de UI:
 * sanitização do que se digita, mapeamento do motivo para a chave i18n e a
 * sugestão de contingência quando o servidor não devolve uma (offline).
 * Puro e sem React para poder ser testado sem DOM.
 */
import {
  slugifyName,
  suggestSlugCandidates,
  TENANT_SLUG_MAX,
  validateTenantSlug,
} from '@self-restaurant/shared'

export type SlugProblemKind = 'format' | 'reserved' | 'taken' | 'too_long'

/** Códigos de erro de slug que a API devolve (console #418 e self-serve). */
const SLUG_ERROR_CODES: Record<string, SlugProblemKind> = {
  SLUG_INVALID: 'format',
  SLUG_RESERVED: 'reserved',
  SLUG_TAKEN: 'taken',
}

type Translator = (key: string, vars?: Record<string, string>) => string

/** Sanitiza o que se digita: sem acento (Café → cafe), minúsculo, só [a-z0-9-]. */
export function sanitizeSlugInput(raw: string): string {
  return (
    raw
      .normalize('NFD')
      // \p{M}: diacríticos combinantes da decomposição NFD de acentos
      .replace(/\p{M}/gu, '')
      .toLowerCase()
      .replace(/[^a-z0-9-]+/g, '-')
      .replace(/-{2,}/g, '-')
  )
}

export type SlugValidation = { ok: true } | { ok: false; kind: Exclude<SlugProblemKind, 'taken'> }

/** Regras do shared — chamada no blur e no submit (não a cada tecla). */
export function validateSlugField(slug: string): SlugValidation {
  // longo demais tem mensagem própria (o shared devolve só 'format')
  if (slug.length > TENANT_SLUG_MAX) return { ok: false, kind: 'too_long' }
  const problem = validateTenantSlug(slug)
  return problem ? { ok: false, kind: problem } : { ok: true }
}

/** Mensagem i18n do motivo (format/reserved/taken/too_long). */
export function slugMessage(kind: SlugProblemKind, t: Translator): string {
  switch (kind) {
    case 'reserved':
      return t('admin.tenant.slug_err_reserved')
    case 'taken':
      return t('admin.tenant.slug_err_taken')
    case 'too_long':
      return t('admin.tenant.slug_err_too_long')
    default:
      return t('admin.tenant.slug_err_format')
  }
}

/**
 * Sugestão sem olhar o banco — contingência quando a API não devolve uma:
 * 1º candidato das regras compartilhadas que é válido e diferente do atual.
 * (Com banco, a API já responde `details.suggestion` — essa vence.)
 */
export function suggestSlugLocal(wanted: string): string {
  const current = sanitizeSlugInput(wanted)
  for (const c of suggestSlugCandidates(wanted)) {
    if (c !== current && validateTenantSlug(c) === null) return c
  }
  return slugifyName(wanted)
}

export type SlugFieldError = {
  kind: SlugProblemKind
  message: string
  suggestion: string | null
}

/**
 * Erro 422/409 de slug da API → erro NO CAMPO (não toast). null = a falha
 * não é de slug (rede, 5xx…) — o chamador mostra o erro geral.
 * O motivo explícito do servidor (details.reason) vence — inclusive
 * 'too_long' (#418 QA); sem reason, um format para slug acima de
 * TENANT_SLUG_MAX vira too_long pelo comprimento enviado.
 */
export function slugFieldErrorFromApi(
  err: unknown,
  t: Translator,
  submittedSlug?: string,
): SlugFieldError | null {
  const code = (err as { code?: string } | null)?.code
  let kind = code ? SLUG_ERROR_CODES[code] : undefined
  if (!kind) return null
  const details = (err as { details?: unknown } | null)?.details as
    { reason?: unknown; suggestion?: unknown } | undefined
  const reason = typeof details?.reason === 'string' ? details.reason : null
  if (reason === 'format' || reason === 'reserved' || reason === 'taken' || reason === 'too_long') {
    kind = reason
  } else if (
    kind === 'format' &&
    typeof submittedSlug === 'string' &&
    submittedSlug.length > TENANT_SLUG_MAX
  ) {
    kind = 'too_long'
  }
  const suggestion =
    typeof details?.suggestion === 'string' && details.suggestion ? details.suggestion : null
  return { kind, message: slugMessage(kind, t), suggestion }
}
